import { useContext, useEffect, useRef, useState } from "react";
import { formatDateToDDMMAAAA } from "../../../../functions/kit-mdtw/dates";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import RefreshIcon from "@mui/icons-material/Refresh";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import SeleccionContext from "../../../../context/Seleccion/SeleccionContext";
import { IPersonaInhabilitada } from "../../../../interfaces/ISeleccion";
import { PROFILE_ADMIN_SEL } from "../../../../diccionary/Diccionary";
import Tooltip from "../../../ui/kit-mdtw/tooltip/Tooltip";
import TableSkeleton from "../../../ui/kit-mdtw/table/TableSkeleton";
import FooterTablePagination from "../../../ui/kit-mdtw/table/FooterTablePagination";
import ActionMassiveInhabilitada from "./ActionMassiveInhabilitada";
import TableSearchInput from "../../../ui/TableSearchInput";
import ModalRestaurarInhabilitada from "./ModalRestaurarInhabilitada";

const LOCAL_STORAGE_FILTER = "filtroPersonaInhabilitada";

const initialFormFilter = {
  dni: [],
  nombre: [],
  tipo: [],
  nivel: [],
  clienteNombre: [],
  areaNegocioCod: [],
  busquedaId: [],
  usuario: [],
  fecha: [],
};

type BadgeConfig = { label: string; bg: string; color: string; dot: string };

const NIVEL_FALLBACK: BadgeConfig = {
  label: "—",
  bg: "#f1f5f9",
  color: "#475569",
  dot: "#94a3b8",
};

const NIVEL_CONFIG: Record<string, BadgeConfig> = {
  ALL: { label: "TEAM-WORK", bg: "#fee2e2", color: "#991b1b", dot: "#ef4444" },
  AUTO: { label: "AUTOMATICO", bg: "#ffedd5", color: "#c2410c", dot: "#f97316" },
  BUSQUEDA: { label: "PROCESO", bg: "#dbeafe", color: "#1d4ed8", dot: "#3b82f6" },
  CLIENTE: { label: "CLIENTE", bg: "#ede9fe", color: "#6d28d9", dot: "#8b5cf6" },
};

const TIPO_BY_NIVEL: Record<string, string> = {
  "Conducta Inadecuada": "ALL",
  "Comparendo": "ALL",
  "Proceso Judicial": "ALL",
  "Antecedentes Penales": "ALL",
  "Rol General": "AUTO",
  "Finiquito": "AUTO",
  "Baja Confirmada": "AUTO",
  "Rol Privado": "AUTO",
  "Candidato desiste": "BUSQUEDA",
  "No cumple perfil": "BUSQUEDA",
  "Cliente no valida Direccion": "CLIENTE",
  "Cliente no valida Rut": "CLIENTE",
};

const resolveNivelBadge = (nivel: string | null | undefined): BadgeConfig => {
  if (!nivel) return NIVEL_FALLBACK;
  const key = String(nivel).trim().toUpperCase();
  return NIVEL_CONFIG[key] ?? { ...NIVEL_FALLBACK, label: String(nivel) };
};

const resolveTipoBadge = (tipo: string | null | undefined): BadgeConfig => {
  if (!tipo) return NIVEL_FALLBACK;
  const nivelKey = TIPO_BY_NIVEL[tipo];
  const base = nivelKey ? NIVEL_CONFIG[nivelKey] : null;
  return base ? { ...base, label: tipo } : { ...NIVEL_FALLBACK, label: tipo };
};

const StatusPill = ({ cfg }: { cfg: BadgeConfig }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      padding: "3px 10px",
      borderRadius: "12px",
      background: cfg.bg,
      color: cfg.color,
      fontSize: "11.5px",
      fontWeight: 600,
      border: `1px solid ${cfg.color}33`,
      whiteSpace: "nowrap",
    }}
  >
    <span
      style={{
        display: "inline-block",
        width: "6px",
        height: "6px",
        borderRadius: "50%",
        background: cfg.dot,
        boxShadow: `0 0 5px ${cfg.dot}`,
        marginRight: "5px",
      }}
    />
    {cfg.label}
  </span>
);

const TablaInhabilitada = () => {
  const {
    profile,
    token,
    listaPersonasInhabilitadas,
    getPersonasInhabilitadas,
  } = useContext(SeleccionContext);

  const [isLoadingTable, setIsLoadingTable] = useState<boolean | undefined>(
    undefined
  );
  const [isErrorTable, setIsErrorTable] = useState<boolean | undefined>(
    undefined
  );
  const [listadoSeleccionado, setListadoSeleccionado] = useState<string[]>([]);
  const [currentCandidates, setCurrentCandidates] = useState<string[]>([]);
  const [openModalRestaurar, setOpenModalRestaurar] = useState<boolean>(false);
  const [candidateSelect, setCandidateSelect] = useState<string[]>([]);
  const [paginationData, setPaginationData] = useState<any>({});
  const [searchValue, setSearchValue] = useState<string>("");
  const searchRef = useRef<string>("");
  const [offset, setOffset] = useState<any>({
    offset: { value: undefined, valid: true },
    pages: 1,
  });
  const [formFilter, setFormFilter] = useState<any>(initialFormFilter);

  useEffect(() => {
    listRefresh();
  }, [token, localStorage.getItem(LOCAL_STORAGE_FILTER), offset.pages, offset.offset, searchValue]);

  const handleSearch = (term: string): void => {
    searchRef.current = term;
    setSearchValue(term);
    setOffset((prev: any) => ({ ...prev, pages: 1 }));
  };

  useEffect(() => {
    if (listaPersonasInhabilitadas) {
      const ids = listaPersonasInhabilitadas.map(
        (item: IPersonaInhabilitada) => item.excPerId
      );
      setCurrentCandidates(ids);
    }
  }, [listaPersonasInhabilitadas]);

  const listRefresh = async (): Promise<void> => {
    if (!token) return;
    const stored = localStorage.getItem(LOCAL_STORAGE_FILTER);
    const filtros = {
      ...(stored ? JSON.parse(stored) : formFilter),
      search: searchRef.current,
    };
    await getPersonasInhabilitadas({
      token,
      filtros,
      setIsErrorTable,
      setIsLoadingTable,
      setPaginationData,
      page: offset.pages ?? 1,
      limit: offset.offset.value?.code ?? 10,
    });
  };

  const handleClickAddOrRemoveChoise = (excPerId: string): void => {
    const arrayList: string[] = [...listadoSeleccionado];
    if (!arrayList.includes(excPerId)) {
      arrayList.push(excPerId);
    } else {
      const position: number = arrayList.findIndex((item) => item === excPerId);
      arrayList.splice(position, 1);
    }
    setListadoSeleccionado(arrayList);
  };

  const handleClickAddAll = (): void => {
    const arrayListado: string[] = [...listadoSeleccionado];
    if (listadoSeleccionado.length < currentCandidates.length) {
      currentCandidates
        .filter((excPerId: string) => !listadoSeleccionado.includes(excPerId))
        .forEach((excPerId: string) => arrayListado.push(excPerId));
    } else {
      arrayListado.splice(0, currentCandidates.length);
    }
    setListadoSeleccionado(arrayListado);
  };

  const handleClickRestaurarRow = (excPerId: string): void => {
    setCandidateSelect([excPerId]);
    setOpenModalRestaurar(true);
  };

  const isAdminSel = PROFILE_ADMIN_SEL.includes(profile);

  return (
    <>
      {isAdminSel ? (
        <section className="content-seleccion-multiple action-in-line">
          <div className="content-multiple">
            <label className="title-multiple">
              <span
                className={`selects-badge${
                  listadoSeleccionado.length === 0 ? " inactive" : ""
                }`}
                style={{
                  transition:
                    "background 0.3s ease, color 0.3s ease, font-weight 0.3s ease",
                }}
              >
                {listadoSeleccionado.length > 0
                  ? `${listadoSeleccionado.length} ítem${
                      listadoSeleccionado.length !== 1 ? "s" : ""
                    } seleccionado${listadoSeleccionado.length !== 1 ? "s" : ""}`
                  : "0 ítems seleccionados"}
              </span>
            </label>
          </div>
          <div
            style={{
              flex: "1 1 auto",
              minWidth: 0,
              display: "flex",
              margin: "0 16px",
            }}
          >
            <TableSearchInput
              value={searchValue}
              onSearch={handleSearch}
              placeholder="Buscar en la tabla…"
              height={40}
            />
          </div>
          <ActionMassiveInhabilitada
            listadoSeleccionado={listadoSeleccionado}
            localStorageFilter={LOCAL_STORAGE_FILTER}
            formFilter={formFilter}
            setFormFilter={setFormFilter}
            listRefresh={listRefresh}
          />
        </section>
      ) : (
        <section className="content-seleccion-multiple action-in-line">
          <div className="content-multiple"></div>
          <div
            style={{
              flex: "1 1 auto",
              minWidth: 0,
              display: "flex",
              margin: "0 16px",
            }}
          >
            <TableSearchInput
              value={searchValue}
              onSearch={handleSearch}
              placeholder="Buscar en la tabla…"
              height={40}
            />
          </div>
          <ActionMassiveInhabilitada
            listadoSeleccionado={listadoSeleccionado}
            localStorageFilter={LOCAL_STORAGE_FILTER}
            formFilter={formFilter}
            setFormFilter={setFormFilter}
            listRefresh={listRefresh}
          />
        </section>
      )}

      <section className="content-header-table">
        <label className="title-header-table">Persona inhabilitadas</label>
      </section>

      {!isLoadingTable ? (
        <>
          {isErrorTable ? (
            <div className="container-is-error-table">
              Error al cargar los datos.
            </div>
          ) : (
            <>
              <main className="container-table-list">
                <table className="content-table">
                  <thead className="thead-table-registros">
                    <tr>
                      {isAdminSel && (
                        <th className="th-table-registros col-w-check">
                          <button
                            onClick={handleClickAddAll}
                            className="button-select"
                          >
                            {listadoSeleccionado.length ===
                              currentCandidates.length &&
                            listadoSeleccionado.length > 0 ? (
                              <CheckBoxIcon />
                            ) : (
                              <CheckBoxOutlineBlankIcon />
                            )}
                          </button>
                        </th>
                      )}
                      <th className="th-table-registros col-w-rut">RUT</th>
                      <th className="th-table-registros">Nombres</th>
                      <th className="th-table-registros">Motivo</th>
                      <th className="th-table-registros">Tipo</th>
                      <th className="th-table-registros">Cliente</th>
                      <th className="th-table-registros">Area Negocio</th>
                      <th className="th-table-registros">Vacante</th>
                      <th className="th-table-registros">Usuario</th>
                      <th className="th-table-registros">Comentario</th>
                      <th className="th-table-registros col-w-fecha">
                        Fecha <br />
                        Inhabilitación
                      </th>
                      {isAdminSel && (
                        <th className="th-table-registros col-w-acciones">
                          Acciones
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="tbody-table-registros">
                    {listaPersonasInhabilitadas &&
                      listaPersonasInhabilitadas.map(
                        (item: IPersonaInhabilitada) => {
                          const {
                            excPerId,
                            dni,
                            nombre,
                            tipo,
                            nivel,
                            clienteNombre,
                            areaNegocioCod,
                            busquedaId,
                            usuario,
                            comentario,
                            fecha,
                          } = item;
                          return (
                            <tr
                              key={excPerId}
                              className={`tr-table-registros ${
                                listadoSeleccionado.includes(excPerId)
                                  ? "checkeado"
                                  : ""
                              }`}
                            >
                              {isAdminSel && (
                                <td className="td-table-registros col-w-check">
                                  <button
                                    className="button-select"
                                    onClick={() =>
                                      handleClickAddOrRemoveChoise(excPerId)
                                    }
                                  >
                                    {listadoSeleccionado.includes(excPerId) ? (
                                      <CheckBoxIcon />
                                    ) : (
                                      <CheckBoxOutlineBlankIcon />
                                    )}
                                  </button>
                                </td>
                              )}
                              <td className="td-table-registros col-w-rut">
                                {dni}
                              </td>
                              <td className="td-table-registros">{nombre}</td>
                              <td className="td-table-registros">
                                <StatusPill cfg={resolveTipoBadge(tipo)} />
                              </td>
                              <td className="td-table-registros">
                                <StatusPill cfg={resolveNivelBadge(nivel)} />
                              </td>
                              <td className="td-table-registros">
                                {clienteNombre ?? "-"}
                              </td>
                              <td className="td-table-registros">
                                {areaNegocioCod ?? "-"}
                              </td>
                              <td className="td-table-registros">
                                {busquedaId ?? "-"}
                              </td>
                              <td className="td-table-registros">{usuario}</td>
                              <td className="td-table-registros">
                                {comentario ? (
                                  <Tooltip text={comentario}>
                                    <ChatBubbleOutlineIcon
                                      style={{
                                        color: "#0065DD",
                                        cursor: "pointer",
                                      }}
                                    />
                                  </Tooltip>
                                ) : (
                                  "-"
                                )}
                              </td>
                              <td className="td-table-registros col-w-fecha">
                                {formatDateToDDMMAAAA({
                                  date: fecha,
                                  format: "/",
                                })}
                              </td>
                              {isAdminSel && (
                                <td className="td-table-registros col-w-acciones">
                                  <section className="content-icon-table">
                                    <Tooltip text="Revertir inhabilitación">
                                      <button
                                        onClick={() =>
                                          handleClickRestaurarRow(excPerId)
                                        }
                                        className="button-content"
                                      >
                                        <RefreshIcon className="restaurar" />
                                      </button>
                                    </Tooltip>
                                  </section>
                                </td>
                              )}
                            </tr>
                          );
                        }
                      )}
                  </tbody>
                </table>
              </main>
              <FooterTablePagination
                paginationData={paginationData}
                offset={offset}
                setOffset={setOffset}
              />
            </>
          )}
        </>
      ) : (
        <TableSkeleton cols={12} />
      )}

      {openModalRestaurar && (
        <ModalRestaurarInhabilitada
          list={candidateSelect}
          listRefresh={listRefresh}
          onChangeBack={() => setOpenModalRestaurar(false)}
          title="REVERTIR INHABILITACIÓN DE CANDIDATOS"
          message="Estás por quitar inhabilidad a la siguiente selección:"
        />
      )}
    </>
  );
};

export default TablaInhabilitada;
